import { z } from 'zod'
import { oc } from '@orpc/contract'
import { BILLING_ENTITY_VALUES } from '../billing-entity.contract'
import { MAX_RANGE_MONTHS } from './list-cases.contract'

// Accounting reports (PD-1970, spec §4 of
// docs/superpowers/specs/2026-10-30-accounting-reports-design.md): the report view, its CSV
// export and the filter options. Dates are YYYY-MM-DD in the billing timezone; dateTo is the
// cutoff. Amounts are cents on the wire — dollars only in the CSV.

export const ACCOUNTING_REPORT_VALUES = ['revenue', 'payments', 'outstanding'] as const
export const ACCOUNTING_GROUP_BY_VALUES = [
  'entity',
  'state',
  'provider',
  'payer',
  'method',
] as const
export const ACCOUNTING_DETAILS_CAP = 5000

/** dateTo ≥ dateFrom and at most MAX_RANGE_MONTHS apart (the list's arithmetic). */
function refineRange(value: { dateFrom: string; dateTo: string }, ctx: z.RefinementCtx) {
  const from = new Date(value.dateFrom)
  const to = new Date(value.dateTo)
  if (to < from) {
    ctx.addIssue({
      code: 'custom',
      path: ['dateTo'],
      message: 'dateTo must not be before dateFrom',
    })
    return
  }
  const max = new Date(from)
  max.setUTCMonth(max.getUTCMonth() + MAX_RANGE_MONTHS)
  if (to > max) {
    ctx.addIssue({
      code: 'custom',
      path: ['dateTo'],
      message: `Date range must not exceed ${MAX_RANGE_MONTHS} months`,
    })
  }
}

export const reportInputSchema = z
  .object({
    dateFrom: z.iso.date(),
    dateTo: z.iso.date(),
    billingEntity: z.array(z.enum(BILLING_ENTITY_VALUES)).optional(),
    patientState: z.array(z.string().length(2)).optional(),
    practitionerId: z.array(z.string()).optional(),
    payerName: z.array(z.string()).optional(),
    /** FHIR Appointment.status values. */
    appointmentStatus: z.array(z.string()).optional(),
    report: z.enum(ACCOUNTING_REPORT_VALUES),
    groupBy: z.enum(ACCOUNTING_GROUP_BY_VALUES),
  })
  .superRefine((value, ctx) => {
    refineRange(value, ctx)
    if (value.groupBy === 'method' && value.report !== 'payments') {
      ctx.addIssue({
        code: 'custom',
        path: ['groupBy'],
        message: "groupBy 'method' is only valid for the payments report",
      })
    }
  })

/** groupKey narrows the export to one summary group ('' is the NULL bucket). */
export const reportExportInputSchema = reportInputSchema.extend({
  groupKey: z.string().optional(),
})

/** A group value of a detail row; key '' is the NULL bucket. */
export const reportDimensionSchema = z.object({
  key: z.string(),
  label: z.string(),
})

const detailBaseShape = {
  encounterId: z.string(),
  claimId: z.string().nullable(),
  patientId: z.string(),
  patientName: z.string(),
  serviceDate: z.string(),
  entity: reportDimensionSchema,
  state: reportDimensionSchema,
  provider: reportDimensionSchema,
  payer: reportDimensionSchema,
}

export const revenueRowSchema = z.object({
  ...detailBaseShape,
  submissionRoute: z.string(),
  claimStatus: z.string(),
  chargesCents: z.number(),
  contractualCents: z.number(),
  otherWriteoffsCents: z.number(),
  afterAdjustmentsCents: z.number(),
})

export const paymentRowSchema = z.object({
  ...detailBaseShape,
  transactionId: z.number(),
  reference: z.string().nullable(),
  activity: z.string(),
  paymentDate: z.string(),
  method: reportDimensionSchema,
  cashCents: z.number(),
})

export const outstandingRowSchema = z.object({
  ...detailBaseShape,
  ageDays: z.number(),
  insuranceArCents: z.number(),
  patientArCents: z.number(),
  totalArCents: z.number(),
})

const reportEnvelopeShape = {
  asOf: z.string(),
  cards: z.object({
    totalChargesCents: z.number(),
    chargesAfterAdjustmentsCents: z.number(),
    paymentsCents: z.number(),
    outstandingCents: z.number(),
  }),
  columns: z.array(z.string()),
  groups: z.array(
    z.object({ key: z.string(), label: z.string(), valuesCents: z.array(z.number()) }),
  ),
  totalCents: z.array(z.number()),
  truncated: z.boolean(),
  detailsTotal: z.number(),
}

export const reportOutputSchema = z.discriminatedUnion('report', [
  z.object({
    ...reportEnvelopeShape,
    report: z.literal('revenue'),
    details: z.array(revenueRowSchema),
  }),
  z.object({
    ...reportEnvelopeShape,
    report: z.literal('payments'),
    details: z.array(paymentRowSchema),
  }),
  z.object({
    ...reportEnvelopeShape,
    report: z.literal('outstanding'),
    details: z.array(outstandingRowSchema),
  }),
])

export const billingV2Report = oc
  .route({ method: 'POST', path: '/v2/billing/report' })
  .input(reportInputSchema)
  .output(reportOutputSchema)

export const billingV2ReportFilterOptions = oc
  .route({ method: 'GET', path: '/v2/billing/report/filter-options' })
  .input(z.object({ dateFrom: z.iso.date(), dateTo: z.iso.date() }).superRefine(refineRange))
  .output(
    z.object({
      providers: z.array(z.object({ id: z.string(), name: z.string() })),
      payers: z.array(z.string()),
      states: z.array(z.string()),
    }),
  )

export const billingV2ReportExport = oc
  .route({ method: 'POST', path: '/v2/billing/report/export' })
  .input(reportExportInputSchema)
  .output(z.file())
